import * as Yup from 'yup';
import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { OpenModalHookType } from '@hooks/useOpenModal';
import { useSelector } from 'react-redux';
import { KioskStatus, TableKiosk, TableKioskType, TableStatus } from '@api/TablesKiosks/TableKiosk';
import { RootReducer } from '@store/index';

interface ModalSaveTableKioskProps {
    modalSaveTableKiosk: OpenModalHookType;
    tableKiosk?: TableKiosk;
    onAddTableKiosk: (tableKiosk: TableKiosk) => void;
}

const saveTableKioskSchema = Yup.object({
    name: Yup.string().required('Nombre es requerido'),
    type: Yup.string().oneOf([TableKioskType.TABLE, TableKioskType.KIOSK]).required('Tipo es requerido'),
    status: Yup.boolean().default(true)
});

export const ModalSaveTableKiosk: React.FunctionComponent<ModalSaveTableKioskProps> = (props) => {
    const { modalSaveTableKiosk, tableKiosk, onAddTableKiosk } = props;
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    const [selectedType, setSelectedType] = useState<TableKioskType>(TableKioskType.TABLE);

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors }
    } = useForm<TableKiosk>({
        resolver: yupResolver(saveTableKioskSchema),
        defaultValues: {
            status: true,
            type: TableKioskType.TABLE,
            branchOfficeId: branchOfficeSelected?.id || ''
        }
    });

    const watchType = watch('type');

    useEffect(() => {
        if (watchType) {
            setSelectedType(watchType);
        }
    }, [watchType]);

    const handleClose = () => {
        modalSaveTableKiosk.toggle();
    };

    const onSaveTableKiosk: SubmitHandler<TableKiosk> = async (values) => {
        const tableKioskData: TableKiosk = {
            ...values,
            id: tableKiosk?.id,
            branchOfficeId: branchOfficeSelected?.id || '',
            tableStatus: values.type === TableKioskType.TABLE ? TableStatus.AVAILABLE : undefined,
            kioskStatus: values.type === TableKioskType.KIOSK ? KioskStatus.ACTIVE : undefined
        };
        onAddTableKiosk(tableKioskData);
    };

    useEffect(() => {
        if (modalSaveTableKiosk.modalOpen) {
            reset({
                id: tableKiosk?.id || undefined,
                name: tableKiosk?.name || '',
                type: tableKiosk?.type || TableKioskType.TABLE,
                status: tableKiosk?.status !== undefined ? tableKiosk.status : true,
                branchOfficeId: branchOfficeSelected?.id || ''
            });
            setSelectedType(tableKiosk?.type || TableKioskType.TABLE);
        }
    }, [modalSaveTableKiosk.modalOpen]);

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 15, padding: 4 }
            }}
            open={modalSaveTableKiosk.modalOpen}
            onClose={handleClose}
            aria-labelledby="modal-save-table-kiosk-title"
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle id="modal-save-table-kiosk-title">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                        {tableKiosk?.id ? 'Editar' : 'Nueva'}{' '}
                        {selectedType === TableKioskType.TABLE ? 'Mesa' : 'Kiosco'}
                    </Typography>
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <form id="save-table-kiosk-form" onSubmit={handleSubmit(onSaveTableKiosk)}>
                    <Stack spacing={2}>
                        <Controller
                            name="type"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <RadioGroup row value={value} onChange={onChange}>
                                    <FormControlLabel value={TableKioskType.TABLE} control={<Radio />} label="Mesa" />
                                    <FormControlLabel value={TableKioskType.KIOSK} control={<Radio />} label="Kiosco" />
                                </RadioGroup>
                            )}
                        />

                        <Stack spacing={2}>
                            <Controller
                                name="name"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label={`Nombre del ${
                                            selectedType === TableKioskType.TABLE ? 'Mesa' : 'Kiosco'
                                        }`}
                                        variant="outlined"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.name}
                                        helperText={errors.name?.message}
                                        placeholder={
                                            selectedType === TableKioskType.TABLE
                                                ? 'Ej: Mesa 1, Terraza, VIP'
                                                : 'Ej: Kiosco 1, Autoservicio'
                                        }
                                    />
                                )}
                            />
                            <Typography variant="body1" fontWeight={500} color="textSecondary">
                                {selectedType === TableKioskType.TABLE
                                    ? 'Una mesa permite recibir múltiples pedidos y acumularlos en un solo ticket. Una mesa puede recibir pedidos desde distintos canales simultáneamente (navegador del cliente, tablet de mesero, tablet de caja, etc...).'
                                    : 'Un kiosco brinda la experiencia de autoservicio para el cliente. El cliente ordena de forma autónoma y puede elegir pagar con tarjeta al momento o contra entrega con terminal o efectivo.'}
                            </Typography>
                        </Stack>
                    </Stack>
                </form>
            </DialogContent>
            <DialogActions>
                <Box flex={1} textAlign="center">
                    <Button
                        sx={{ width: 300 }}
                        color="primary"
                        variant="contained"
                        type="submit"
                        form="save-table-kiosk-form"
                    >
                        {tableKiosk?.id ? 'Actualizar' : 'Agregar'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};
